import * as Sentry from "@sentry/react";

const IGNORED_STATUS_CODES = new Set([401, 406]);
const IGNORED_PATH_PATTERNS: RegExp[] = [/\/auth\/v1\/token/];
const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const MAX_BODY_LENGTH = 2048;

const getOperationLabel = (method: string, url: URL): string => {
  const pathname = url.pathname
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      ":id",
    )
    .replace(/\/\d+(?=\/|$)/g, "/:id");

  return `${method.toUpperCase()} ${pathname}`;
};

const truncate = (value: string): string =>
  value.length > MAX_BODY_LENGTH ? `${value.slice(0, MAX_BODY_LENGTH)}...` : value;

const extractRequestBody = (init: RequestInit | undefined): string | undefined => {
  if (!init?.body) {
    return undefined;
  }

  if (typeof init.body === "string") {
    return truncate(init.body);
  }

  return undefined;
};

const extractResponseInfo = async (
  response: Response,
): Promise<{ body?: string; message?: string }> => {
  try {
    const cloned = response.clone();
    const text = await cloned.text();

    if (!text) {
      return {};
    }

    const body = truncate(text);

    try {
      const json = JSON.parse(text) as Record<string, unknown>;
      const message =
        (json.message as string) ??
        (json.error as string) ??
        (json.msg as string) ??
        (json.error_description as string);

      if (typeof message === "string") {
        return { body, message };
      }
    } catch {
      // no-op: response is not JSON.
    }

    return { body };
  } catch {
    return {};
  }
};

const normalizeForFingerprint = (message: string): string =>
  message
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ":id")
    .replace(/\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}[:\d.]*/g, ":timestamp")
    .replace(/\b\d+\b/g, ":n")
    .trim()
    .slice(0, 200);

const resolveUrl = (input: RequestInfo | URL): string => {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof URL) {
    return input.href;
  }

  return input.url;
};

export const createSentryFetch = (): typeof globalThis.fetch => {
  const nativeFetch = globalThis.fetch.bind(globalThis);

  const sentryFetch: typeof globalThis.fetch = async (input, init) => {
    const method = (init?.method ?? "GET").toUpperCase();
    let url: URL;

    try {
      url = new URL(resolveUrl(input));
    } catch {
      return nativeFetch(input, init);
    }

    const operation = getOperationLabel(method, url);
    const response = await nativeFetch(input, init);

    Sentry.addBreadcrumb({
      type: "http",
      category: "supabase",
      message: `${operation} -> ${response.status}`,
      level: response.ok ? "info" : response.status >= 500 ? "error" : "warning",
      data: {
        method,
        url: url.toString(),
        status_code: response.status,
      },
    });

    if (response.ok) {
      return response;
    }

    if (IGNORED_STATUS_CODES.has(response.status)) {
      return response;
    }

    if (IGNORED_PATH_PATTERNS.some((pattern) => pattern.test(url.pathname))) {
      return response;
    }

    const { body: responseBody, message: errorMessage } = await extractResponseInfo(response);
    const requestBody = MUTATION_METHODS.has(method) ? extractRequestBody(init) : undefined;

    Sentry.withScope((scope) => {
      scope.setLevel(response.status >= 500 ? "error" : "warning");
      scope.setTag("http.status_code", String(response.status));
      scope.setTag("http.method", method);
      scope.setTag("supabase.operation", operation);
      scope.setContext("Supabase Request", {
        method,
        url: url.toString(),
        pathname: url.pathname,
        status: response.status,
        statusText: response.statusText,
        ...(requestBody ? { requestBody } : {}),
      });

      if (responseBody) {
        scope.setContext("Supabase Response", {
          body: responseBody,
          ...(errorMessage ? { message: errorMessage } : {}),
        });
      }

      const fingerprint = ["supabase-http-error", operation, String(response.status)];

      if (errorMessage) {
        fingerprint.push(normalizeForFingerprint(errorMessage));
      }

      scope.setFingerprint(fingerprint);

      const error = new Error(
        `Supabase ${response.status}: ${operation}${errorMessage ? ` - ${errorMessage}` : ""}`,
      );
      error.name = "SupabaseHttpError";

      Sentry.captureException(error);
    });

    return response;
  };

  return sentryFetch;
};
